import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import { globalPutService } from '../../../utils/globalApiServices';
import { checkApiStatus } from '../../../utils';
import { revertInvoiceStatusApi } from '../apiService';
import { trackActivity } from '../../../utils/mixpanel';

const revertStatusOptions = [
    { label: 'Sent', value: 2 },
    { label: 'Draft', value: 1 },
];

const RevertInvoiceStatus = ({ revertStatusInv, onToggleModal, getResponseBack }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setLoading] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const currentStatusValue = revertStatusInv?.data?.status?.value;

    const availableStatusOptions = useMemo(() => {
        if(revertStatusInv?.data?.management_fee_invoice?.id) return revertStatusOptions.filter(option => option.value !== 1);
        return revertStatusOptions.filter(option => !(currentStatusValue === 2 && option.value === 2));
    }, [currentStatusValue]);

    useEffect(() => {
        if (!revertStatusInv?.modal) return;
        setSelectedStatus(availableStatusOptions[0] || null);
    }, [availableStatusOptions, revertStatusInv?.modal]);

    const handleSubmit = () => {
        if (!revertStatusInv?.data?.slug || !selectedStatus) return;

        setLoading(true);
        const payload = {
            slug: revertStatusInv?.data?.slug,
            status: selectedStatus,
        };

        globalPutService(revertInvoiceStatusApi(), payload)
            .then(response => {
                if (checkApiStatus(response)) {
                    trackActivity('Item Restored', { page_title: 'Revert Invoice Status', success_msg: response?.data?.message, item_ids: revertStatusInv?.data, response: response?.data, });
                    enqueueSnackbar(response?.data?.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' }, });
                    onToggleModal?.();
                    getResponseBack?.();
                } else {
                    setLoading(false);
                    enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' }, });
                    trackActivity('Item Restore', { page_title: 'Revert Invoice Status', error_msg: response?.data?.message, item_ids: revertStatusInv?.data, response: response?.data, });
                }
            })
            .catch(() => setLoading(false));
    };

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Revert Invoice Status', page_type: page_type });
    };

    const handleClose = () => {
        if (isLoading) return;
        setSelectedStatus(null);
        onToggleModal?.();
    };

    return (
        <Dialog
            aria-labelledby="revert-invoice-status-dialog-title"
            className="add-lease-dialog"
            maxWidth="xs"
            open={!!revertStatusInv?.modal}
            fullWidth
        >
            <DialogTitle sx={{ m: 0, p: 2 }} className="dialog-header" id="revert-invoice-status-dialog-title">
                Revert Invoice Status
            </DialogTitle>
            <DialogContent dividers >
                <Grid container spacing={2}>
                    <Grid item size={{ md: 12, xs: 12 }}>
                        <Autocomplete
                            disablePortal
                            options={availableStatusOptions}
                            getOptionLabel={(option) => option?.label || ''}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            value={selectedStatus}
                            onChange={(e, newValue) => setSelectedStatus(newValue)}
                            disableClearable
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    label="Revert Status To"
                                    margin="normal"
                                    size="small"
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ paddingRight: '16px' }}>
                <Button className="cancel-btn" variant="outlined" size="small" disabled={isLoading} onClick={() => { handleClose(); handleItemClicked('Cancel Button') }}>
                    Cancel
                </Button>
                <Button variant="contained" className="btn-primary" disabled={isLoading || !selectedStatus} size="small" onClick={() => { handleSubmit(); handleItemClicked('Save Button') }} >
                    {isLoading ? <CircularProgress color="inherit" size={25} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RevertInvoiceStatus;
